import { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from '@emotion/styled';
import { tokens, Text, Button, Icon, Modal } from '@hotjar/hotsauce';
import type { Site } from '@hotjar/site';
import { getPageType, getRouteConfigByPath } from '@hotjar/webapp/utils/routing';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTrackingCodeState } from '@hotjar/webapp/hooks/useTrackingCode';
import { useGettingStartedGuide } from '@hotjar/webapp/hooks/useGettingStartedGuide';
import {
  useGSGStore,
  selectStoreById,
} from '@hotjar/webapp/domains/activation/getting-started-guide';
import { FEEDBACK, SURVEYS } from '@hotjar/webapp/constants/routes';
import type { CurrentUser } from '@hotjar/user';
import type { Step } from './types';
import { StepId, StepState } from './types';
import { Wizard } from './components';
import { useGettingStartedState } from './hooks/useGettingStartedState';
import { useCelebrations } from './hooks/useCelebrations';
import { SideBarWizard } from './components/SideBarWizard';

const Actions = styled.div`
  display: flex;
  gap: ${tokens.spacer.x1pt5};

  > * {
    margin-top: ${tokens.spacer.x1pt5};
  }
`;

const StepsWrapper = styled.ul`
  list-style: none;
  margin: ${tokens.spacer.x1} 0 0;
  padding: 0;
  color: ${tokens.color.text_light};
  ${tokens.text('body2')};
`;

const StepContainer = styled.li<{ completed: boolean }>`
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  counter-increment: finish-installation-step;
  text-decoration: ${({ completed }) => (completed ? 'line-through' : 'none')};

  &:before {
    display: ${({ completed }) => (completed ? 'none' : 'flex')};
    content: counter(finish-installation-step);
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 16px;
    font-weight: 600;
    font-size: 12px;
    margin-right: ${tokens.spacer.x1};
    color: ${tokens.color.white};
    background: ${tokens.color.text_light};
  }
`;

const CompletedStepIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x1};
`;

const GettingStartedGuideWizard = ({
  site,
  user,
  inSideBar,
}: {
  site: Site;
  user: CurrentUser;
  inSideBar?: boolean;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const { installed } = useTrackingCodeState();
  const { markAsCompleted, markStepAsCompleted } = useGettingStartedGuide();
  const { click: trackClickEvent, onboardingEnded: trackOnboardingEnded } = useTrack();
  const trackClick = useCallback(
    (elementName: string) => trackClickEvent(elementName, 'Getting Started Guide', {}),
    [trackClickEvent],
  );
  const siteId = site?.id;
  const siteUrlAdded = !!site?.url;
  const { steps, remaining, total, expandedStepIndex, setExpandedStepIndex, isStepsReady } =
    useGettingStartedState(site, user);
  const [isDismissed, setIsDismissed] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const { fireworksOriginRef } = useCelebrations(site, remaining === 0 && total > 0);

  const onStepCTAClick = (currentStep: Step) => {
    trackClick(`${currentStep.id} CTA`);
    if (currentStep.cta?.completeOnClick) {
      markStepAsCompleted(currentStep.id);
      const currentStepIndex = steps.indexOf(currentStep);
      const nextPendingStep = steps.find(
        (step: Step, index: number) => step.state === StepState.Pending && index > currentStepIndex,
      );
      setExpandedStepIndex(nextPendingStep ? steps.indexOf(nextPendingStep) : undefined);
    }
  };
  const skipGuide = () => {
    setIsDismissed(true);
    setConfirmationModalOpen(false);
    trackClick('Confirm Skip');
    trackOnboardingEnded();
    markAsCompleted();
  };
  const onSkipClick = () => {
    setConfirmationModalOpen(true);
    trackClick('Skip');
  };
  const onToggleSection = (index: number) => {
    const open = expandedStepIndex === index;
    setExpandedStepIndex(index);
    trackClick(open ? `Expand ${index} section` : `Collapse ${index} section`);
  };
  const onGuideToggle = useCallback(
    (isCollapsed: boolean) => {
      trackClick(isCollapsed ? 'Collapse Guide' : 'Expand Guide');
    },
    [trackClick],
  );

  const WizardLayout = inSideBar ? SideBarWizard : Wizard;

  if (isDismissed || !isStepsReady) return null;

  return (
    <>
      <WizardLayout
        ref={inSideBar ? undefined : fireworksOriginRef}
        title={t('activation:getting-started.wizard-title')}
        banner={
          expandedStepIndex !== undefined && steps[expandedStepIndex]
            ? steps[expandedStepIndex].banner
            : undefined
        }
        remainingTasks={remaining}
        sectionToShow={expandedStepIndex}
        onDismiss={installed ? onSkipClick : undefined}
        onToggle={onGuideToggle}
        onToggleSection={onToggleSection}
      >
        {steps.map((step) => (
          <Wizard.Section
            key={step.id}
            title={t(step.title)}
            icon={step.icon}
            status={step.state === StepState.Success ? 1 : undefined}
          >
            <Text textType="body2" textColor="text_light">
              {step.content && t(step.content)}
            </Text>
            {step.id === StepId.FinishInstallation ? (
              <StepsWrapper>
                <StepContainer completed={siteUrlAdded}>
                  {siteUrlAdded ? (
                    <CompletedStepIcon icon="check_alt" size="m" color="text_success" />
                  ) : null}
                  {t('activation:getting-started.tips.add-url.subtitle')}
                </StepContainer>
                <StepContainer completed={installed}>
                  {installed ? (
                    <CompletedStepIcon icon="check_alt" size="m" color="text_success" />
                  ) : null}
                  {t('activation:getting-started.tips.install-hotjar.subtitle')}
                </StepContainer>
              </StepsWrapper>
            ) : null}
            <Actions>
              {siteId && step.cta && (
                <Button
                  kind="primary"
                  as="a"
                  size="s"
                  icon={step.cta.icon}
                  isExternal={!!step.cta.isExternal}
                  href={step.cta.url}
                  onClick={() => onStepCTAClick(step)}
                >
                  {t(step.cta.title)}
                </Button>
              )}
              {siteId &&
                step.ctaSecondary &&
                step.ctaSecondary.url !== window.location.pathname && (
                  <Button
                    kind="secondary"
                    as="a"
                    size="s"
                    icon={step.ctaSecondary.icon}
                    isExternal={!!step.ctaSecondary.isExternal}
                    href={step.ctaSecondary.url}
                    onClick={() => trackClick(`${step.id} secondary CTA`)}
                  >
                    {t(step.ctaSecondary.title)}
                  </Button>
                )}
            </Actions>
          </Wizard.Section>
        ))}
      </WizardLayout>
      <Modal
        contentLabel={t('activation:getting-started.skip-confirmation-modal.label')}
        isOpen={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
      >
        <Modal.Header
          title={t('activation:getting-started.skip-confirmation-modal.wizard-title')}
        />
        <Modal.Body>
          <Trans i18nKey="activation:getting-started.skip-confirmation-modal.wizard-content">
            If you dismiss it, you won&apos;t see <b>Get started with Hotjar</b> again.
          </Trans>
        </Modal.Body>
        <Modal.Footer>
          <Button kind="ghost" onClick={() => setConfirmationModalOpen(false)}>
            {t('activation:getting-started.skip-confirmation-modal.cancel')}
          </Button>
          <Button kind="primary" onClick={skipGuide}>
            {t('activation:getting-started.skip-confirmation-modal.dismiss-guide')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

type GettingStartedGuideProps = {
  inSideBar?: boolean;
};

export const GettingStartedGuide = ({
  inSideBar,
}: GettingStartedGuideProps): JSX.Element | null => {
  const routeConfig = getRouteConfigByPath(window.location.pathname);
  const pageType = getPageType(routeConfig?.name ?? '');
  const isFeaturePage = pageType === 'feature';
  const isNewJourneyPage =
    isFeaturePage &&
    (routeConfig?.name === SURVEYS.CREATE.name || routeConfig?.name === FEEDBACK.CREATE.name);
  const { init } = useGettingStartedGuide();
  const { data: currentSite } = useCurrentSite();
  const { data: currentUser } = useCurrentUser();
  const gettingStartedGuideState = useGSGStore(
    // We use currentSite id so we don't cause re-renders if a new ref
    // to currentSite is provided with the same id
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useCallback((state) => selectStoreById(state, currentSite), [currentSite?.id]),
  );

  useEffect(() => {
    if (currentSite?.id && currentUser?.id && isFeaturePage) {
      init();
    }
  }, [currentSite?.id, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (
    isNewJourneyPage ||
    !isFeaturePage ||
    !currentSite ||
    !currentUser ||
    !gettingStartedGuideState.enabled
  )
    return null;

  return <GettingStartedGuideWizard inSideBar={inSideBar} site={currentSite} user={currentUser} />;
};
